import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, FormCheck, Spinner, Table } from 'react-bootstrap';
import {
  ContentPackAvailabilityType,
  ContentPackExplicitContentStatus,
  ContentSearchType,
} from '../../../../../__gqltypes__';
import AddContentPack from './AddContentPack';
import ContentPackExplicitBadge from './ContentPackExplicitBadge';
import ImportContentPackList from './ImportContentPackList';

type Props = {
  id?: string;
  contentPacks: {
    id: string;
    popularity: number;
    explicitContentStatus?: ContentPackExplicitContentStatus | null;
    name: string;
  }[];
  updatePackIds: (packIds: string[], selection: Record<string, string[]>) => Promise<void>;
  enableImport?: boolean;
  enableContinuousUpdate?: boolean;
  defaultSearchType?: ContentSearchType;
  defaultAvailabilityType?: ContentPackAvailabilityType;
  saveLabel?: string;
  loading?: boolean;
  // Lets a parent save the list itself (e.g. one Save for the whole screen) instead of through the button
  onPacksChange?: (packIds: string[]) => void;
  hideSaveButton?: boolean;

  // If provided, add each name as a column with a checkbox, and add this data in the callback
  checkboxColumnNames?: string[];
};

const ContentPackListEditor = ({
  id,
  contentPacks,
  updatePackIds,
  enableImport,
  enableContinuousUpdate,
  defaultSearchType,
  defaultAvailabilityType,
  saveLabel,
  loading,
  checkboxColumnNames,
  onPacksChange,
  hideSaveButton,
}: Props) => {
  const [curPacks, setCurPacks] = useState(contentPacks);
  const [checkboxSelection, setCheckboxSelection] = useState<Record<string, string[]>>({});

  // Load content packs auto-generated by backend after clearing list
  useEffect(() => {
    if (curPacks.length === 0 || enableContinuousUpdate) {
      setCurPacks(contentPacks);
    }
  }, [contentPacks, enableContinuousUpdate]);

  useEffect(() => {
    setCurPacks(contentPacks);
  }, [id]);

  useEffect(() => {
    onPacksChange?.(curPacks.map((pack) => pack.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curPacks]);

  const modified = useMemo(() => {
    return contentPacks.length !== curPacks.length || contentPacks.some((pack, idx) => curPacks[idx].id !== pack.id);
  }, [contentPacks, curPacks]);

  const add = useCallback(
    (pack) => {
      setCurPacks(curPacks.concat([pack]));
    },
    [curPacks]
  );
  const remove = useCallback(
    (packId) => {
      const newPacks = [...curPacks];
      newPacks.splice(
        curPacks.findIndex((pack) => pack.id === packId),
        1
      );
      setCurPacks(newPacks);
    },
    [curPacks]
  );

  const onToggleRowCheck = (column: string, rowId: string) => {
    console.log(column, rowId, checkboxSelection, (checkboxSelection[column] || []).includes(rowId));
    setCheckboxSelection((pSelection) => ({
      ...pSelection,
      [column]: (pSelection[column] || []).includes(rowId)
        ? (pSelection[column] || []).filter((id) => id !== rowId)
        : [...(pSelection[column] || []), rowId],
    }));
  };

  const moveUp = useCallback(
    (idx) => {
      const newPacks = [...curPacks];
      const [pack] = newPacks.splice(idx, 1);
      newPacks.splice(idx - 1, 0, pack);
      setCurPacks(newPacks);
    },
    [curPacks]
  );
  const moveDown = useCallback(
    (idx) => {
      const newPacks = [...curPacks];
      const [pack] = newPacks.splice(idx, 1);
      newPacks.splice(idx + 1, 0, pack);
      setCurPacks(newPacks);
    },
    [curPacks]
  );

  const removeIdx = useCallback(
    (idx) => {
      const newPacks = [...curPacks];
      newPacks.splice(idx, 1);
      setCurPacks(newPacks);
    },
    [curPacks]
  );

  const [submitting, setSubmitting] = useState(false);
  const save = useCallback(async () => {
    setSubmitting(true);
    try {
      await updatePackIds(
        curPacks.map((pack) => pack.id),
        checkboxSelection
      );
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  }, [curPacks, updatePackIds, checkboxSelection]);

  const isSorted = useMemo(() => {
    let hasLess = false;
    for (let i = 1; i < curPacks.length; i++) {
      const prev = curPacks[i - 1].popularity;
      const cur = curPacks[i].popularity;
      if (cur < prev) {
        hasLess = true;
      } else if (cur > prev) {
        return false;
      }
    }
    return hasLess;
  }, [curPacks]);

  const sortByPop = useCallback(async () => {
    const packs = [...curPacks];
    packs.sort((a, b) => (isSorted ? -1 : 1) * (b.popularity - a.popularity));
    setCurPacks(packs);
  }, [curPacks, isSorted]);

  return (
    <div className="cp-list">
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <h4>Content Packs</h4>
        {hideSaveButton ? null : modified ? (
          <Button variant="primary" disabled={submitting || loading} onClick={save}>
            {submitting || loading ? (
              <Spinner as="span" animation="border" size="sm" className="mr-2" />
            ) : (
              <i className="fas fa-save mr-2" />
            )}
            {saveLabel ?? 'Save'}
          </Button>
        ) : (
          <Button variant="success" disabled>
            <i className="fas fa-check mr-2" />
            Saved
          </Button>
        )}
      </div>
      <Table bordered hover>
        <thead>
          <tr>
            <th className="text-center">#</th>
            <th>Id</th>
            <th>Name</th>
            <th className="text-center">Pop.</th>
            <th />
            {(checkboxColumnNames || []).map((name) => (
              <th className="text-center" key={name}>
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {curPacks.map((cp, idx) => (
            <tr key={`${cp.id}.${idx}`}>
              <td className="text-center">{idx + 1}</td>
              <td>{cp.id}</td>
              <td>
                {cp.name}
                <ContentPackExplicitBadge explicitStatus={cp.explicitContentStatus || null} />
              </td>
              <td className="text-center">{cp.popularity}</td>
              <td className="p-0 align-middle text-center">
                <ButtonGroup size="sm">
                  <Button variant="info" onClick={() => moveUp(idx)} disabled={idx === 0}>
                    <i className="fas fa-arrow-up" />
                  </Button>
                  <Button variant="info" onClick={() => moveDown(idx)} disabled={idx === curPacks.length - 1}>
                    <i className="fas fa-arrow-down" />
                  </Button>
                </ButtonGroup>
                <Button variant="danger" onClick={() => removeIdx(idx)} className="square-icon icon-sm">
                  <i className="fas fa-times" />
                </Button>
              </td>
              {(checkboxColumnNames || []).map((name) => (
                <td className="text-center" key={name}>
                  <FormCheck id={`${name}-${id}-check`} onChange={() => onToggleRowCheck(name, cp.id)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="text-center">
        <AddContentPack
          add={add}
          remove={remove}
          curPacks={curPacks}
          defaultSearchType={defaultSearchType}
          defaultAvailabilityType={defaultAvailabilityType}
        />
        <Button variant="info" onClick={sortByPop} className="ml-1" disabled={curPacks.length < 2}>
          <i className={`fas mr-2 fa-sort-amount-${isSorted ? 'up' : 'down'}`} />
          Sort by popularity
        </Button>
        {enableImport && <ImportContentPackList doImport={setCurPacks} />}
      </div>
    </div>
  );
};

export default ContentPackListEditor;
